import { Modal, Typography, Spin, message } from 'antd';
import { BarIcon, Heading } from '@/components';
import { useTranslation } from 'react-i18next';
import Dragger from 'antd/es/upload/Dragger';
import { useUploadBulkResolveExcel } from '@/hooksApi/useImport';
import { useFileUpload } from '@/hooks';

const { Paragraph, Link } = Typography;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BulkResolveModal = ({ isOpen, onClose }: ModalProps) => {
  const { t } = useTranslation();

  const { mutate: UploadExcel, isLoading: loadingExcel } = useUploadBulkResolveExcel();

  const { handleBeforeUpload, uploadFile, uploadedFile, resetFile } = useFileUpload(() => {
    message.success(String(t('upload_Success')));
    handleClose();
  });

  const handleSubmit = () => {
    uploadFile(UploadExcel);
  };

  const handleClose = () => {
    onClose();
    resetFile();
  };

  return (
    <Modal
      className='rounded-[20px] bg-white border border-customGrayShade3'
      title={
        <div
          className='flex gap-x-1'
          style={{ borderBottom: '1px solid #E1E1E3', paddingBottom: '19px' }}
        >
          <span className='font-bold text-lg text-[rgb(57,57,60)] leading-[20.88px] uppercase'>
            {t('bulk_resolve')}
          </span>
        </div>
      }
      width={760}
      centered
      footer={null}
      open={isOpen}
      onOk={handleClose}
      onCancel={handleClose}
    >
      <div className='flex flex-col gap-6 mt-4'>
        <Heading title={t('import_instructions')} level={5} upperCase />
        <div className='flex flex-col'>
          <Paragraph>
            <strong>1. {t('download_template')}:</strong> {t('start_download')}{' '}
            <Link href='/boxy-bulk-order-resolve.csv' target='_blank'>
              {t('csv_template')}
            </Link>
            .
          </Paragraph>
          <Paragraph>
            <strong>2. {t('fill_data')}:</strong> {t('use_first')}.
          </Paragraph>
          <Paragraph>
            <strong>3. {t('import_limit')}:</strong> {t('can_import')}.
          </Paragraph>
          {/* <Card className='bg-customGrayShade1'>
            <Heading title={t('how_to_work')} level={5} />
            <Paragraph>
              {t('importing')} <Link href='#'>{t('csv_guide')}</Link>.
            </Paragraph>
          </Card> */}
        </div>

        {loadingExcel ? (
          <Spin />
        ) : (
          <div className='flex flex-col mb-6'>
            <Heading title={t('upload_file')} level={5} upperCase />

            <Dragger
              name={t('upload_file')}
              multiple={false}
              maxCount={1}
              beforeUpload={handleBeforeUpload}
              accept='.csv, .xlsx'
            >
              <BarIcon className='my-4' />
              <div className='flex flex-col items-center justify-center'>
                <div className='flex gap-x-1'>
                  <Heading
                    title={t('click_to_upload')}
                    fontWeight='font-bold'
                    fontSize='text-base'
                    extraClassName='underline'
                  />
                  <Heading title={t('drag_drop')} fontWeight='font-bold' fontSize='text-base' />
                </div>
                <Heading
                  title={t('maximum_size')}
                  fontSize='text-sm'
                  color='text-[rgb(115,123,140)]'
                />
              </div>
            </Dragger>
          </div>
        )}

        <div className='flex gap-4 justify-end'>
          <button
            type='button'
            onClick={handleClose}
            className='h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
          >
            <Heading
              title={t('cancel')}
              fontWeight='font-bold'
              fontSize='text-sm'
              extraClassName='uppercase leading-5 text-[rgb(57,57,60)]'
            />
          </button>
          <button
            type='button'
            onClick={handleSubmit}
            disabled={!uploadedFile}
            className='h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors disabled:opacity-60'
          >
            <Heading
              title={t('submit')}
              fontWeight='font-bold'
              fontSize='text-sm'
              extraClassName='uppercase leading-5 text-[rgb(57,57,60)]'
            />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkResolveModal;
